import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  P,
  SeoPageShell,
} from "../seo/SeoPageLayout";
import AnalysisExampleBlock from "@/components/seo/AnalysisExampleBlock";
import CrossLinkFooter from "@/components/seo/AnalysisCrossLinkFooter";

const Bullet = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <li className="mt-3 text-[17px] leading-[1.7] text-white/75">
    <span className="text-white font-semibold">{title}</span> {children}
  </li>
);

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is DARVO in a text-message conversation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "DARVO stands for Deny, Attack, Reverse Victim and Offender. In texts it looks like: the original issue is flatly denied, the subject pivots to something you did, and by the end you're the one explaining or apologizing.",
      },
    },
    {
      "@type": "Question",
      name: "How does the 'Deny' step of DARVO show up over text?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The original issue you raised gets flatly denied or reframed as not having happened the way you described it — often with phrases like 'I never said that' or 'that's not what happened.'",
      },
    },
    {
      "@type": "Question",
      name: "What does the 'Attack' step of DARVO look like?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Almost immediately after the denial, the conversation turns to something you did. A real or exaggerated flaw of yours gets introduced as if it's equally relevant to the original concern.",
      },
    },
    {
      "@type": "Question",
      name: "Can DARVO happen in a single text message?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. DARVO doesn't need three separate conversations — a single sentence can deny the original issue, attack you, and reverse the victim/offender roles in one breath.",
      },
    },
  ],
};

const DarvoInTextMessages = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Is This DARVO? Deny, Attack, Reverse — Spotted in Real Texts"
        description="A non-apology isn't an apology. See how DARVO — Deny, Attack, Reverse Victim and Offender — actually plays out in text messages."
        path="/analysis/darvo-in-text-messages"
        jsonLd={FAQ_SCHEMA}
      />

      <HeroH1>Is This DARVO? Analyzing Deny-Attack-Reverse Patterns in Texts</HeroH1>

      <H2>The Quick Diagnostic</H2>
      <P>DARVO has a specific three-part shape — once you know it, it's hard to miss:</P>
      <ul className="list-none pl-0 mt-2">
        <Bullet title="Deny.">
          The original issue gets flatly denied or reframed as not having happened the way you
          described it.
        </Bullet>
        <Bullet title="Attack.">
          Almost immediately, the conversation turns to something <em>you</em> did — a real or
          exaggerated flaw gets introduced as if it's equally relevant.
        </Bullet>
        <Bullet title="Reverse.">
          By the end, you're the one explaining or apologizing — the roles have fully swapped, and
          the person who caused the original concern is now the one who seems wronged.
        </Bullet>
        <Bullet title="It happens fast, often in one message.">
          DARVO doesn't need three separate conversations — a single sentence can deny, attack, and
          reverse in one breath.
        </Bullet>
      </ul>

      <H2>What it looks like in a real exchange</H2>
      <AnalysisExampleBlock
        turns={[
          { speaker: "you", text: "You said you'd call me back and you never did." },
          {
            speaker: "them",
            tone: "toxic",
            text: "I never said that. And honestly, you're always keeping score of every little thing — it's exhausting to be around.",
          },
        ]}
        mechanismLabel="[!] Phase 1: Structural Denial → Attack Pivot"
        mechanismDescription="In two sentences: the original claim is denied outright, then the subject flips to a character accusation about you. By the time the message ends, you're not discussing the missed call — you're defending yourself against 'keeping score.'"
      />

      <H2>The breakdown</H2>
      <P>
        DARVO works because it happens faster than most people can track in real time — by the
        time you notice the subject changed, you're already several exchanges into defending the
        new topic instead of the original one. The name itself is the map:{" "}
        <strong className="text-white">D</strong>eny removes the thing you brought up,{" "}
        <strong className="text-white">A</strong>ttack introduces a new grievance about you,{" "}
        <strong className="text-white">R</strong>everse completes the swap so you're now managing
        their feelings instead of yours. Naming the sequence while it's happening is often the
        only way to stop it from working.
      </P>

      <H2>Why it happens</H2>
      <P>
        DARVO isn't always a calculated strategy. Often it's shame doing the work — accountability
        gets experienced as an unbearable threat to self-image, so the emotional charge gets
        redirected outward instead of inward. That's why it can happen just as easily as an
        automatic, habitual reaction as a deliberate manipulation. Either way, the structure — and
        the effect on the person hearing it — is the same: the conversation's subject quietly
        shifts from "what happened" to "what's wrong with you for bringing it up," often within a
        single sentence, before either person has had time to think it through.
      </P>

      <H2>What to do about it</H2>
      <P>
        Naming the pattern while it's happening is the most effective response — even just
        internally noticing "this just moved from what I raised to what's wrong with me" can be
        enough to keep from getting pulled off-topic. If you want to keep the conversation on
        track, return to the original point once, without re-litigating whatever counter-accusation
        just came up. If repeated attempts to return to the topic keep getting met with more DARVO,
        it's reasonable to disengage rather than keep trying. And if this is a repeating pattern
        rather than a one-off, it's worth talking to someone outside the situation — a trusted
        friend or a professional — rather than trying to resolve it alone.
      </P>

      <H2>See it in your own conversation</H2>

      <P>
        You just read a fictional example. If a real exchange has been sitting with you, paste it
        into the analyzer — no signup required for your first look.
      </P>
      <CTAButton />

      <CrossLinkFooter current="darvo" />
    </SeoPageShell>
  );
};

export default DarvoInTextMessages;
